import { defineStore } from 'pinia'
import { useToastStore } from '@/stores/toast'
import { buildAddressPayload, transformAddressForForm } from '@/helpers/address-helper'
import { switchMandateErrorText } from '@/helpers/invoice-helper'
import {
  getInvoices,
  getInvoice,
  getInvoiceChildInvoices,
  cancelInvoice,
  syncInvoicePaymentStatus,
  sendInvoiceToCustomer,
  switchInvoiceCollectionMandate,
  setInvoiceAutosend,
  getInvoiceAuditLog,
  getInvoiceTransitions,
  updateInvoice,
  completePayment as completePaymentRequest,
  createAdjustmentItem,
  updateAdjustmentItem,
  deleteAdjustmentItem,
  deleteInvoice as deleteInvoiceRequest,
  finalizeInvoice,
  exportInvoicesDatev,
  createInvoice,
  getEligibleChildInvoices,
  previewCollectiveDraft as previewCollectiveDraftRequest,
  createCollectiveFromCancelled as createCollectiveFromCancelledRequest
} from '@/services/entities/invoice'

type InvoicesStore = {
  items: InvoiceListItem[]
  totalItems: number
  loading: boolean
  currentInvoice: InvoiceEntity | null
  currentInvoiceLoading: boolean
  childInvoices: InvoiceEntity[]
  childInvoicesTotal: number
  childInvoicesLoading: boolean
  actionLoading: boolean
  auditLog: InvoiceAuditLogEntry[]
  auditLogLoading: boolean
  transitions: InvoiceTransitionEntry[]
  transitionsLoading: boolean
  datevLoading: boolean
}

const toIsoDate = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate()
  ).padStart(2, '0')}`

const handleError = (error: unknown, toastStore: ReturnType<typeof useToastStore>): void => {
  const detail =
    typeof error === 'object' && error !== null
      ? (error as { _error?: string; message?: string })._error ??
        (error as { message?: string }).message ??
        'An error occurred.'
      : 'An error occurred.'
  toastStore.add({ severity: 'error', summary: 'Error', detail })
}

// API-Platform ValidationException returns 422 with a `violations` array
// ({ propertyPath, message }); surface them for inline display in the composer.
const extractViolations = (error: unknown): InvoiceComposerViolation[] => {
  const data = (error as { response?: { data?: { violations?: InvoiceComposerViolation[] } } })
    ?.response?.data
  return Array.isArray(data?.violations) ? (data.violations as InvoiceComposerViolation[]) : []
}

const triggerBlobDownload = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  window.URL.revokeObjectURL(url)
}

const LIST_GROUPS = [
  'invoice:list',
  'calculable:list',
  'customer:read',
  'company:read',
  'user:list',
  'cancelable:read',
  'timestampable'
]

const DETAIL_GROUPS = [
  'invoice:read',
  'checkoutable:read',
  'calculable:read',
  'item:read',
  // Order-type items (SalesInvoice charged items): the LEAN order:list projection
  // gives orderName + status only. Deliberately NOT order:read / order:nested:read
  // / product:read — those expand the full Product graph + nested-order tree +
  // unused ManyToMany collections per order (N+1, very slow). quantity comes from
  // item:read, prices from calculable:read.
  'order:list',
  'customer:read',
  'company:read',
  'cart:read',
  'user:read',
  'cancelable:read',
  'payment:read',
  // Payment amount/status/currency/dueDate live on the parent Transaction
  'transaction:read',
  // Accounting fields (vatId lives on billingAddress) + invoicingNote/autosend
  'pm:read',
  // Nested DunningProcess fields (level / status / nextLevelDate)
  'dunning-process:read',
  // cancelledInvoice + cancelledInvoiceCopies (bounded by MaxDepth) — the
  // cancelled-CI <-> replacement-CI cross links shown on the detail view.
  'invoice:nested:read',
  'timestampable'
]

export const useInvoicesStore = defineStore('invoices', {
  state: (): InvoicesStore => ({
    items: [],
    totalItems: 0,
    loading: false,
    currentInvoice: null,
    currentInvoiceLoading: false,
    childInvoices: [],
    childInvoicesTotal: 0,
    childInvoicesLoading: false,
    actionLoading: false,
    auditLog: [],
    auditLogLoading: false,
    transitions: [],
    transitionsLoading: false,
    datevLoading: false
  }),

  actions: {
    async fetchItems(
      page: number,
      itemsPerPage: number,
      filters: Record<string, string | string[] | number | boolean | null> = {},
      sort: Record<string, 'ASC' | 'DESC'> | null = null,
      groups: string[] = LIST_GROUPS,
      basePath: string = '/invoices'
    ) {
      const filterParams: any[] = Object.entries(filters)
        .filter(([, v]) => v !== null && v !== undefined && !(Array.isArray(v) && v.length === 0))
        .flatMap(([key, value]) =>
          Array.isArray(value) ? value.map((v) => ({ [key]: v })) : [{ [key]: value }]
        )

      const params: any[] = [
        { page, itemsPerPage },
        ...groups.map((group) => ({ 'groups[]': group })),
        ...filterParams
      ]

      // Invoice's OrderFilter uses orderParameterName=orderBy (vs. the default
      // `order` used elsewhere), so we emit `orderBy[field]=DIR` directly —
      // exactly as the credit-notes store does. Don't touch shared fetch.ts.
      if (sort) {
        const sortParam: Record<string, string> = {}
        Object.keys(sort).forEach((key) => {
          sortParam[`orderBy[${key}]`] = sort[key]
        })
        params.push(sortParam)
      }

      this.loading = true
      try {
        const response = await getInvoices(params, basePath)
        this.items = response['hydra:member'] || []
        this.totalItems = response['hydra:totalItems']
      } catch (error) {
        this.items = []
        this.totalItems = 0
        throw error
      } finally {
        this.loading = false
      }
    },

    async fetchInvoice(id: string, groups: string[] = DETAIL_GROUPS) {
      this.currentInvoiceLoading = true
      try {
        const params = groups.map((g) => ({ 'groups[]': g }))
        this.currentInvoice = await getInvoice(id, params)
      } catch (error) {
        this.currentInvoice = null
        throw error
      } finally {
        this.currentInvoiceLoading = false
      }
    },

    async fetchChildInvoices(
      id: string,
      page: number = 1,
      itemsPerPage: number = 10,
      sort: Record<string, 'ASC' | 'DESC'> = { name: 'ASC' },
      groups: string[] = [
        'invoice:read',
        'item:read',
        'calculable:read',
        'cart:read',
        'customer:read'
      ]
    ) {
      // Paginated (default 10/page, ordered by invoice name). Never fetch the
      // whole collective at once — a large Sammelrechnung would blow up memory.
      this.childInvoicesLoading = true
      try {
        const params: any[] = [{ page, itemsPerPage }, ...groups.map((g) => ({ 'groups[]': g }))]
        const sortParam: Record<string, string> = {}
        Object.keys(sort).forEach((key) => {
          sortParam[`orderBy[${key}]`] = sort[key]
        })
        params.push(sortParam)

        const { items, total } = await getInvoiceChildInvoices(id, params)
        this.childInvoices = items
        this.childInvoicesTotal = total
      } catch (error) {
        this.childInvoices = []
        this.childInvoicesTotal = 0
        throw error
      } finally {
        this.childInvoicesLoading = false
      }
    },

    async fetchTransitions(id: string): Promise<void> {
      this.transitionsLoading = true
      try {
        this.transitions = await getInvoiceTransitions(id)
      } catch (error) {
        this.transitions = []
        throw error
      } finally {
        this.transitionsLoading = false
      }
    },

    async updateBillingAddress(id: string, billingAddress: Record<string, unknown>): Promise<void> {
      const toastStore = useToastStore()
      this.actionLoading = true
      try {
        await updateInvoice(id, { billingAddress })
        toastStore.success('Billing address updated.')
      } catch (error) {
        handleError(error, toastStore)
        throw error
      } finally {
        this.actionLoading = false
      }
    },

    async updateNote(id: string, invoicingNote: string): Promise<void> {
      const toastStore = useToastStore()
      this.actionLoading = true
      try {
        await updateInvoice(id, { invoicingNote })
        toastStore.success('Invoice note updated.')
      } catch (error) {
        handleError(error, toastStore)
        throw error
      } finally {
        this.actionLoading = false
      }
    },

    async updateAccounting(
      id: string,
      payload: {
        vatId: string | null
        accountNumber: string | null
        counterAccountNumber: string | null
      }
    ): Promise<void> {
      const toastStore = useToastStore()
      this.actionLoading = true
      try {
        // VAT ID is stored on the billingAddress JSON map, so merge it into the
        // existing address (BillingAddressRequirements needs every canonical key)
        // and set it explicitly. accountNumber/counterAccountNumber are direct
        // pm:write fields. standard_put:false merges only what we send.
        const existing = this.currentInvoice?.billingAddress ?? null
        const billingAddress = buildAddressPayload(
          { ...(transformAddressForForm(existing) ?? {}) },
          existing
        )
        billingAddress.vatId = payload.vatId
        await updateInvoice(id, {
          accountNumber: payload.accountNumber,
          counterAccountNumber: payload.counterAccountNumber,
          billingAddress
        })
        toastStore.success('Accounting updated.')
      } catch (error) {
        handleError(error, toastStore)
        throw error
      } finally {
        this.actionLoading = false
      }
    },

    async addAdjustmentItems(payloads: Array<Record<string, unknown>>): Promise<void> {
      const toastStore = useToastStore()
      this.actionLoading = true
      try {
        await Promise.all(payloads.map((p) => createAdjustmentItem(p)))
        toastStore.success(payloads.length > 1 ? 'Items added.' : 'Item added.')
      } catch (error) {
        handleError(error, toastStore)
        throw error
      } finally {
        this.actionLoading = false
      }
    },

    async updateAdjustmentItem(itemId: string, payload: Record<string, unknown>): Promise<void> {
      const toastStore = useToastStore()
      this.actionLoading = true
      try {
        await updateAdjustmentItem(itemId, payload)
        toastStore.success('Item updated.')
      } catch (error) {
        handleError(error, toastStore)
        throw error
      } finally {
        this.actionLoading = false
      }
    },

    async removeAdjustmentItem(itemId: string): Promise<void> {
      const toastStore = useToastStore()
      this.actionLoading = true
      try {
        await deleteAdjustmentItem(itemId)
        toastStore.success('Item removed.')
      } catch (error) {
        handleError(error, toastStore)
        throw error
      } finally {
        this.actionLoading = false
      }
    },

    async deleteInvoice(id: string): Promise<void> {
      const toastStore = useToastStore()
      this.actionLoading = true
      try {
        await deleteInvoiceRequest(id)
        toastStore.success('Invoice deleted.')
      } catch (error) {
        handleError(error, toastStore)
        throw error
      } finally {
        this.actionLoading = false
      }
    },

    // --- Collective Invoice Editor (#1966) ---
    async fetchEligibleChildren(companyId: string | null): Promise<InvoiceEntity[]> {
      // Collectives are company-scoped; without a company there is no eligible
      // pool. The fetch filters by company server-side (no global page window).
      if (!companyId) {
        return []
      }
      try {
        return await getEligibleChildInvoices(companyId)
      } catch {
        return []
      }
    },

    async previewCollectiveDraft(
      id: string,
      payload: InvoiceDraftPayload,
      mode: 'download' | 'view' = 'download'
    ): Promise<void> {
      const toastStore = useToastStore()
      this.actionLoading = true
      try {
        const blob = await previewCollectiveDraftRequest(id, payload)
        if (mode === 'view') {
          const url = URL.createObjectURL(blob)
          window.open(url, '_blank', 'noopener')
          // Give the new tab time to load before releasing the object URL.
          setTimeout(() => URL.revokeObjectURL(url), 60000)
        } else {
          triggerBlobDownload(blob, `collective-invoice-draft-${id}.pdf`)
        }
      } catch (error) {
        const violations = extractViolations(error)
        if (violations.length === 0) handleError(error, toastStore)
        const enhanced = new Error('Draft preview failed') as Error & {
          violations?: InvoiceComposerViolation[]
        }
        enhanced.violations = violations
        throw enhanced
      } finally {
        this.actionLoading = false
      }
    },

    async createCollectiveFromCancelled(
      id: string,
      payload: InvoiceDraftPayload
    ): Promise<{ id: string; name: string | null; status: string; sent: boolean }> {
      const toastStore = useToastStore()
      this.actionLoading = true
      try {
        const result = await createCollectiveFromCancelledRequest(id, payload)
        toastStore.success('Collective invoice composed.')
        return result
      } catch (error) {
        const violations = extractViolations(error)
        if (violations.length === 0) handleError(error, toastStore)
        const enhanced = new Error('Compose failed') as Error & {
          violations?: InvoiceComposerViolation[]
        }
        enhanced.violations = violations
        throw enhanced
      } finally {
        this.actionLoading = false
      }
    },

    async finalize(typePath: string, id: string): Promise<void> {
      const toastStore = useToastStore()
      this.actionLoading = true
      try {
        await finalizeInvoice(typePath, id)
        toastStore.success('Invoice finalized.')
      } catch (error) {
        handleError(error, toastStore)
        throw error
      } finally {
        this.actionLoading = false
      }
    },

    async completePayment(paymentId: string): Promise<void> {
      const toastStore = useToastStore()
      this.actionLoading = true
      try {
        await completePaymentRequest(paymentId)
        toastStore.success('Payment completed.')
      } catch (error) {
        handleError(error, toastStore)
        throw error
      } finally {
        this.actionLoading = false
      }
    },

    async cancel(id: string, payload: InvoiceCancelPayload): Promise<void> {
      const toastStore = useToastStore()
      this.actionLoading = true
      try {
        await cancelInvoice(id, payload)
        toastStore.success('Invoice cancelled.')
      } catch (error) {
        handleError(error, toastStore)
        throw error
      } finally {
        this.actionLoading = false
      }
    },

    async syncPaymentStatus(id: string): Promise<void> {
      const toastStore = useToastStore()
      this.actionLoading = true
      try {
        await syncInvoicePaymentStatus(id)
        toastStore.success('Payment status synced.')
      } catch (error) {
        handleError(error, toastStore)
        throw error
      } finally {
        this.actionLoading = false
      }
    },

    async sendToCustomer(id: string): Promise<void> {
      const toastStore = useToastStore()
      this.actionLoading = true
      try {
        await sendInvoiceToCustomer(id)
        toastStore.success('Invoice sent to customer.')
      } catch (error) {
        handleError(error, toastStore)
        throw error
      } finally {
        this.actionLoading = false
      }
    },

    async switchCollectionMandate(id: string): Promise<void> {
      const toastStore = useToastStore()
      this.actionLoading = true
      try {
        await switchInvoiceCollectionMandate(id)
        toastStore.success(
          'Switched to the new SEPA mandate. The customer was sent the debit date.'
        )
      } catch (error) {
        const key = (error as { _error?: string } | null)?._error
        handleError({ _error: switchMandateErrorText(key) ?? key }, toastStore)
        throw error
      } finally {
        this.actionLoading = false
      }
    },

    async setAutosend(id: string, autosend: boolean): Promise<void> {
      const toastStore = useToastStore()
      this.actionLoading = true
      try {
        await setInvoiceAutosend(id, autosend)
        toastStore.success(autosend ? 'Auto-send enabled.' : 'Auto-send disabled.')
      } catch (error) {
        handleError(error, toastStore)
        throw error
      } finally {
        this.actionLoading = false
      }
    },

    async fetchAuditLog(id: string): Promise<void> {
      this.auditLogLoading = true
      try {
        this.auditLog = await getInvoiceAuditLog(id)
      } catch (error) {
        this.auditLog = []
        throw error
      } finally {
        this.auditLogLoading = false
      }
    },

    async exportDatev(fromDate: Date, toDate: Date): Promise<void> {
      const toastStore = useToastStore()
      this.datevLoading = true
      try {
        const blob = await exportInvoicesDatev(fromDate, toDate)
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `invoices_datev_${toIsoDate(fromDate)}_to_${toIsoDate(toDate)}.xlsx`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      } catch (error) {
        handleError(error, toastStore)
        throw error
      } finally {
        this.datevLoading = false
      }
    },

    async create(typePath: string, customerIri: string, cartIri?: string): Promise<InvoiceEntity> {
      const toastStore = useToastStore()
      this.actionLoading = true
      try {
        const invoice = await createInvoice(typePath, customerIri, cartIri)
        toastStore.success('Invoice created.')
        return invoice
      } catch (error) {
        handleError(error, toastStore)
        throw error
      } finally {
        this.actionLoading = false
      }
    }
  }
})
